import { useState } from "react";
import { Link } from "react-router-dom";
import { useOrders, STATUS_LABEL, STATUS_PILL, type OrderStatus } from "@/lib/orders";
import NeueBestellungDrawer from "./NeueBestellungDrawer";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

const ALL_STATUS: OrderStatus[] = ["placed", "planned", "in_delivery", "delivered", "failed", "cancelled"];

export default function BestellungenPage() {
  const [statusFilter, setStatusFilter] = useState<OrderStatus[]>(["placed", "planned", "in_delivery"]);
  const [dateFilter, setDateFilter] = useState<string>("");
  const [newOpen, setNewOpen] = useState(false);

  const { data: orders = [], isLoading } = useOrders({
    status: statusFilter.length ? statusFilter : undefined,
    date: dateFilter || undefined,
  });

  function toggleStatus(s: OrderStatus) {
    setStatusFilter((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-hof-900">Bestellungen</h1>
        <button onClick={() => setNewOpen(true)} className="btn-primary">+ Neue Bestellung</button>
      </div>

      <div className="card space-y-3">
        <div>
          <label className="text-sm font-medium text-hof-700">Status:</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {ALL_STATUS.map((s) => (
              <button key={s} onClick={() => toggleStatus(s)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  statusFilter.includes(s)
                    ? "border-wiese-500 bg-wiese-50 text-wiese-800"
                    : "border-hof-200 text-hof-600 hover:bg-hof-50"
                }`}>
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-hof-700">Liefertag:</label>
          <input type="date" className="input max-w-xs"
            value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          {dateFilter && (
            <button onClick={() => setDateFilter("")} className="btn-ghost p-2">✕</button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="card">Lade…</div>
      ) : orders.length === 0 ? (
        <div className="card text-center text-hof-600">Keine Bestellungen mit diesen Filtern.</div>
      ) : (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-hof-50 text-sm text-hof-600">
              <tr>
                <th className="p-3">Nr.</th>
                <th className="p-3">Liefertag</th>
                <th className="p-3">Kunde</th>
                <th className="p-3">Adresse</th>
                <th className="p-3">Status</th>
                <th className="p-3">Zahlart</th>
                <th className="p-3 text-right">Summe</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-hof-100 hover:bg-hof-50/50">
                  <td className="p-3">
                    <Link to={`/backoffice/bestellungen/${o.id}`} className="text-wiese-700 hover:underline font-mono text-sm">
                      {o.order_number ?? "—"}
                    </Link>
                  </td>
                  <td className="p-3">{dfmt.format(new Date(o.delivery_date))}</td>
                  <td className="p-3">{o.customer?.display_name ?? "—"}</td>
                  <td className="p-3 text-sm text-hof-600">
                    {o.address ? `${o.address.zip} ${o.address.city}` : "—"}
                  </td>
                  <td className="p-3"><span className={STATUS_PILL[o.status]}>{STATUS_LABEL[o.status]}</span></td>
                  <td className="p-3 text-sm">{shortMethod(o.payment_method)}</td>
                  <td className="p-3 text-right font-mono">{fmt.format(o.total_cents / 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && <NeueBestellungDrawer onClose={() => setNewOpen(false)} />}
    </div>
  );
}

function shortMethod(m: string) {
  return ({ cash: "Bar", stripe: "Karte", paypal: "PayPal", sepa: "SEPA", invoice: "Rg." } as Record<string, string>)[m] ?? m;
}
